import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CouponsAPI } from '../../../../api/payments/coupons/api';
import { PlansAPI } from '../../../../api/payments/plans/api';
import {
  CouponScopeEnum,
  CouponTypeEnum,
  CouponValueTypeEnum,
} from '../../../../api/payments/coupons/domain/enum';
import {
  CouponScopeLabels,
  CouponTypeLabels,
  CouponValueTypeLabels,
} from '../../../../api/payments/coupons/domain/labels';
import { addYears } from 'date-fns';
import { MAX_COUPON_CYCLE } from '../utils';
import { getRuleCardId } from '../_components/CouponRulesOverview';
import { v4 as uuidv4 } from 'uuid';
import {
  toDateInputValue,
  dateInputValueToIso,
} from '../../../../helpers/dateInput';

const TYPE_OPTIONS = [
  {
    value: CouponTypeEnum.DISCOUNT,
    label: CouponTypeLabels[CouponTypeEnum.DISCOUNT],
  },
  {
    value: CouponTypeEnum.ENROLLMENT,
    label: CouponTypeLabels[CouponTypeEnum.ENROLLMENT],
  },
];

const VALUE_TYPE_OPTIONS = [
  {
    value: CouponValueTypeEnum.PERCENT,
    label: CouponValueTypeLabels[CouponValueTypeEnum.PERCENT],
  },
  {
    value: CouponValueTypeEnum.FIXED,
    label: CouponValueTypeLabels[CouponValueTypeEnum.FIXED],
  },
];

// Cupons miram items: o seletor oferece Geral/Plano/Produto (item). ITEM usa
// itemId (ItemSelect); não há mais escopo/targeting por addon.
const APPLIES_TO_OPTIONS = [
  {
    value: CouponScopeEnum.ALL,
    label: CouponScopeLabels[CouponScopeEnum.ALL],
  },
  {
    value: CouponScopeEnum.PLAN,
    label: CouponScopeLabels[CouponScopeEnum.PLAN],
  },
  { value: CouponScopeEnum.ITEM, label: 'Produto' },
];

function emptyRule() {
  return {
    _key: uuidv4(),
    id: null,
    valueType: CouponValueTypeEnum.PERCENT,
    value: '',
    scope: CouponScopeEnum.ALL,
    fromCycle: 1,
    toCycle: '',
    planId: '',
    itemId: '',
    // UI-only: nome do item selecionado (resumo do form).
    _itemName: '',
    // UI-only: 'discount' | 'finalPrice' — nunca vai pra API (ruleForApi
    // mapeia campos nomeados explicitamente, não faz spread de `rule`).
    _priceInputMode: 'finalPrice',
    // Texto bruto digitado no modo "Preço final desejado" — campo próprio
    // (não derivado de `value` a cada render) pra permitir apagar o input
    // por completo enquanto o usuário redigita.
    _desiredPriceText: '',
  };
}

function mapDiscountToRule(discount) {
  return {
    _key: uuidv4(),
    id: discount.id ?? null,
    valueType:
      discount.valueType ?? discount.value_type ?? CouponValueTypeEnum.PERCENT,
    value: discount.value ?? '',
    scope: discount.scope ?? CouponScopeEnum.ALL,
    fromCycle: discount.fromCycle ?? 1,
    toCycle: discount.toCycle ?? '',
    planId: discount.planId ?? '',
    itemId: discount.itemId ?? '',
    _itemName: discount.itemName ?? '',
    _priceInputMode: 'finalPrice',
    _desiredPriceText: '',
  };
}

function ruleForApi(rule) {
  return {
    valueType: rule.valueType,
    value: Number(rule.value),
    scope: rule.scope,
    fromCycle:
      rule.fromCycle === '' || rule.fromCycle == null
        ? 1
        : Number(rule.fromCycle),
    toCycle:
      rule.toCycle === '' || rule.toCycle == null ? null : Number(rule.toCycle),
    planId: rule.scope === CouponScopeEnum.PLAN ? rule.planId : '',
    itemId: rule.scope === CouponScopeEnum.ITEM ? rule.itemId : '',
  };
}

function rulesAreEqual(a, b) {
  return (
    a.valueType === b.valueType &&
    Number(a.value) === Number(b.value) &&
    a.scope === b.scope &&
    Number(a.fromCycle) === Number(b.fromCycle) &&
    (a.toCycle || '') === (b.toCycle || '') &&
    (a.planId || '') === (b.planId || '') &&
    (a.itemId || '') === (b.itemId || '')
  );
}

export function useAddCouponBootstrap(couponId, initialCoupon) {
  const history = useHistory();
  const originalRulesRef = useRef([]);
  const [notFound, setNotFound] = useState(false);
  const [couponFromState, setCouponFromState] = useState(initialCoupon ?? null);
  const [fetchingCoupon, setFetchingCoupon] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    if (!couponId || couponFromState) return;
    setFetchingCoupon(true);
    CouponsAPI.get(couponId)
      .then((response) => setCouponFromState(response.data))
      .catch(() => {
        toast.warn(
          'Não foi possível carregar os dados do cupom, tente novamente pela listagem.',
        );
        setNotFound(true);
      })
      .finally(() => setFetchingCoupon(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId]);

  // Busca compartilhada, uma única vez: evita que cada linha de regra
  // (PlanSelect) dispare sua própria requisição da lista inteira.
  useEffect(() => {
    PlansAPI.list()
      .then((response) => setPlans(response.data.content || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  // Memoizado: emptyRule()/mapDiscountToRule() geram um _key (uuid) novo a
  // cada chamada. Sem o useMemo, initialValues seria recriado (com _keys
  // diferentes) em todo render, e o enableReinitialize do Formik — que
  // reseta o form quando initialValues muda — entraria em loop infinito.
  const initialValues = useMemo(
    () => ({
      code: couponFromState?.code ?? '',
      active: couponFromState?.active ?? true,
      type: couponFromState?.type ?? CouponTypeEnum.DISCOUNT,
      cycleDays: couponFromState?.cycleDays ?? '',
      startsAt:
        toDateInputValue(couponFromState?.startsAt) ||
        toDateInputValue(new Date()),
      expiresAt:
        toDateInputValue(couponFromState?.expiresAt) ||
        // cupom novo: sugere 1 ano de validade a partir de hoje, pra não deixar em branco por padrão
        (couponFromState ? '' : toDateInputValue(addYears(new Date(), 1))),
      description:
        couponFromState?.description ?? couponFromState?.observation ?? '',
      rules:
        couponFromState?.rules?.length > 0
          ? couponFromState.rules.map(mapDiscountToRule)
          : [emptyRule()],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [couponFromState],
  );

  originalRulesRef.current = initialValues.rules.filter((rule) => rule.id);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      code: yup
        .string()
        .required('O campo nome do cupom é obrigatório!')
        .matches(
          /^[A-Z0-9_-]+$/,
          'Use apenas letras maiúsculas, números, "_" ou "-".',
        ),
      type: yup
        .string()
        .oneOf(Object.values(CouponTypeEnum))
        .required('O campo tipo do cupom é obrigatório!'),
      cycleDays: yup
        .number()
        .typeError('Informe a duração do ciclo em dias')
        .when('type', {
          is: CouponTypeEnum.ENROLLMENT,
          then: (schema) =>
            schema
              .required('A duração do ciclo é obrigatória!')
              .moreThan(0, 'A duração do ciclo deve ser maior que zero'),
          otherwise: (schema) => schema.notRequired(),
        }),
      startsAt: yup.string().required('A data de início é obrigatória!'),
      expiresAt: yup
        .string()
        .required('A data de expiração é obrigatória!')
        .test(
          'not-before-start',
          'A expiração deve ser igual ou posterior ao início',
          function(value) {
            const { startsAt } = this.parent;
            // datas 'yyyy-MM-dd': comparação de string preserva a ordem
            return !value || !startsAt || value >= startsAt;
          },
        ),
      active: yup.boolean().required('O campo ativo é obrigatório!'),
      description: yup.string(),
      rules: yup.array().of(
        yup.object().shape({
          value: yup
            .number()
            .typeError('Informe um valor válido')
            .required('O valor do desconto é obrigatório!')
            .moreThan(0, 'O valor do desconto deve ser maior que zero')
            .when('valueType', {
              is: CouponValueTypeEnum.PERCENT,
              then: (schema) =>
                schema.max(100, 'O percentual não pode passar de 100%'),
            }),
          fromCycle: yup
            .number()
            .typeError('Informe o ciclo inicial')
            .integer('Informe um ciclo válido')
            .min(1, 'O ciclo inicial deve ser no mínimo 1')
            .max(
              MAX_COUPON_CYCLE,
              `O ciclo não pode passar de ${MAX_COUPON_CYCLE}`,
            )
            .required('O ciclo inicial é obrigatório!'),
          toCycle: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === '' ? null : value,
            )
            .nullable()
            .integer('Informe um ciclo válido')
            .min(
              yup.ref('fromCycle'),
              'O ciclo final deve ser maior ou igual ao inicial',
            )
            .max(
              MAX_COUPON_CYCLE,
              `O ciclo não pode passar de ${MAX_COUPON_CYCLE}`,
            ),
          planId: yup.string(),
          itemId: yup.string().when('scope', {
            is: CouponScopeEnum.ITEM,
            then: (schema) => schema.required('Informe o produto'),
          }),
          scope: yup
            .string()
            .required('Defina a regra de aplicação de desconto!'),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const isEnrollment = values.type === CouponTypeEnum.ENROLLMENT;
        const couponPayload = {
          code: values.code,
          active: values.active,
          startsAt: dateInputValueToIso(values.startsAt, false),
          description: values.description,
          type: values.type,
          cycleDays: isEnrollment ? Number(values.cycleDays) : null,
          expiresAt: dateInputValueToIso(values.expiresAt),
        };

        let id = couponId;
        if (couponId) {
          await CouponsAPI.update(couponId, couponPayload);
        } else {
          const response = await CouponsAPI.create(couponPayload);
          id = response.data.id;
        }

        const originalRules = originalRulesRef.current;
        const currentRules = values.rules;

        const rulesToCreate = currentRules.filter((rule) => !rule.id);
        const rulesToDelete = originalRules.filter(
          (original) => !currentRules.some((rule) => rule.id === original.id),
        );
        const rulesToUpdate = currentRules.filter((rule) => {
          if (!rule.id) return false;
          const original = originalRules.find((o) => o.id === rule.id);
          return original && !rulesAreEqual(original, rule);
        });

        const [createdResponses] = await Promise.all([
          Promise.all(
            rulesToCreate.map((rule) =>
              CouponsAPI.createRule(id, ruleForApi(rule)),
            ),
          ),
          ...rulesToUpdate.map((rule) =>
            CouponsAPI.updateRule(id, rule.id, ruleForApi(rule)),
          ),
          ...rulesToDelete.map((rule) => CouponsAPI.deleteRule(id, rule.id)),
        ]);

        const keptRules = currentRules
          .filter((rule) => rule.id)
          .map((rule) => ({
            ...ruleForApi(rule),
            itemName: rule._itemName || undefined,
            id: rule.id,
          }));
        const createdRules = rulesToCreate.map((rule, i) => ({
          ...ruleForApi(rule),
          itemName: rule._itemName || undefined,
          id: createdResponses[i]?.data?.id ?? null,
        }));

        const coupon = {
          id,
          code: values.code,
          active: values.active,
          type: couponPayload.type,
          cycleDays: couponPayload.cycleDays,
          startsAt: couponPayload.startsAt,
          expiresAt: couponPayload.expiresAt,
          description: values.description,
          rules: [...keptRules, ...createdRules],
        };

        toast.success('Cupom salvo com sucesso!');
        history.push(`/payments/coupons/${id}`, { coupon });
      } catch (error) {
        // erros específicos já são exibidos via toast pelas funções de api
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Submit inválido: o card com erro pode estar fora da tela e o Salvar parece
  // não fazer nada — rola até o primeiro erro (campos do topo ou de regra).
  useEffect(() => {
    if (formik.isSubmitting || formik.submitCount === 0 || formik.isValid) {
      return;
    }
    document
      .querySelector('.jds-scope .text-danger')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.isSubmitting]);

  // Planos referenciados por alguma regra que não estão na lista já
  // carregada (fora da 1ª página, ou escolhidos via busca no PlanSelect) —
  // busca e cacheia individualmente, pra DiscountPricePreview sempre achar
  // o plano, inclusive no fluxo de edição.
  const [extraPlansById, setExtraPlansById] = useState({});

  useEffect(() => {
    const missingIds = [
      ...new Set(
        formik.values.rules
          .filter((rule) => rule.scope === CouponScopeEnum.PLAN && rule.planId)
          .map((rule) => rule.planId)
          .filter(
            (id) =>
              !plans.some((plan) => plan.id === id) && !extraPlansById[id],
          ),
      ),
    ];
    if (missingIds.length === 0) return;
    Promise.all(
      missingIds.map((id) =>
        PlansAPI.get(id)
          .then((response) => [id, response.data])
          .catch(() => [id, null]),
      ),
    ).then((entries) => {
      setExtraPlansById((prev) => {
        const next = { ...prev };
        entries.forEach(([id, plan]) => {
          if (plan) next[id] = plan;
        });
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.rules, plans]);

  const addRule = () => {
    const rule = emptyRule();
    formik.setFieldValue('rules', [...formik.values.rules, rule]);
    // Espera o card renderizar; leva a tela e o foco até o desconto novo
    // (o botão fica abaixo da lista, o card novo nasce fora da vista).
    setTimeout(() => {
      const card = document.getElementById(getRuleCardId(rule));
      card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card?.querySelector('button')?.focus({ preventScroll: true });
    }, 0);
  };

  const removeRule = (index) => {
    const rules = formik.values.rules.filter((_, i) => i !== index);
    formik.setFieldValue('rules', rules.length > 0 ? rules : [emptyRule()]);
  };

  const updateRule = (index, field, value) => {
    const rules = formik.values.rules.map((rule, i) =>
      i === index ? { ...rule, [field]: value } : rule,
    );
    formik.setFieldValue('rules', rules);
  };

  const duplicateRule = (index) => {
    const rules = [...formik.values.rules];
    rules.splice(index + 1, 0, { ...rules[index], _key: uuidv4(), id: null });
    formik.setFieldValue('rules', rules);
  };

  const setRuleCycleRange = (index, fromCycle, toCycle) => {
    const rules = formik.values.rules.map((rule, i) =>
      i === index ? { ...rule, fromCycle, toCycle } : rule,
    );
    formik.setFieldValue('rules', rules);
  };

  // Grava o texto bruto digitado (pra exibição, aceita ficar vazio enquanto
  // o usuário apaga/redigita) e o desconto já calculado a partir dele, juntos
  // — mesma razão de sempre: duas chamadas separadas de updateRule se
  // pisariam por causa de closure desatualizada.
  const setRuleDesiredPrice = (index, text, discountValue) => {
    const rules = formik.values.rules.map((rule, i) =>
      i === index
        ? { ...rule, _desiredPriceText: text, value: discountValue }
        : rule,
    );
    formik.setFieldValue('rules', rules);
  };

  // Troca de plano: no modo "Preço final desejado", o desconto gravado foi
  // calculado em cima do preço do plano anterior — deixa de fazer sentido
  // pro plano novo, então limpa (em vez de reinterpretar em silêncio) e
  // exige nova entrada. No modo "Valor do desconto" o valor é plano-agnóstico
  // e continua valendo, só troca o planId mesmo.
  const updateRulePlan = (index, planId) => {
    const rules = formik.values.rules.map((rule, i) => {
      if (i !== index) return rule;
      if (rule._priceInputMode !== 'finalPrice') {
        return { ...rule, planId };
      }
      return { ...rule, planId, value: '', _desiredPriceText: '' };
    });
    formik.setFieldValue('rules', rules);
  };

  const updateRuleItem = (index, itemId, itemName = '') => {
    const rules = formik.values.rules.map((rule, i) =>
      i === index ? { ...rule, itemId, _itemName: itemName } : rule,
    );
    formik.setFieldValue('rules', rules);
  };

  // Atualiza scope e valueType juntos: evita que duas chamadas sequenciais de
  // updateRule (uma pra cada campo) se pisem por causa de closure desatualizada.
  const updateRuleScope = (index, scope) => {
    const rules = formik.values.rules.map((rule, i) =>
      i === index ? { ...rule, scope } : rule,
    );
    formik.setFieldValue('rules', rules);
  };

  const handleCancel = () => {
    if (
      formik.dirty &&
      !window.confirm('Há alterações não salvas. Deseja descartá-las?')
    ) {
      return;
    }
    history.push('/payments/coupons');
  };

  return {
    notFound,
    fetchingCoupon,
    formik,
    plans,
    plansLoading,
    extraPlansById,
    addRule,
    removeRule,
    updateRule,
    duplicateRule,
    updateRuleScope,
    setRuleCycleRange,
    setRuleDesiredPrice,
    updateRulePlan,
    updateRuleItem,
    handleCancel,
    valueTypeOptions: VALUE_TYPE_OPTIONS,
    appliesToOptions: APPLIES_TO_OPTIONS,
    typeOptions: TYPE_OPTIONS,
  };
}
